import { ButtonHTMLAttributes, FC } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import { LoadingIndicator } from './loading-indicator';
const buttonVariants = cva('cursor-pointer inline-flex items-center gap-x-2 text-sm rounded-lg border focus:outline-hidden focus-visible:outline-none focus-visible:ring-1 disabled:pointer-events-none disabled:opacity-50', {
  variants: {
    variant: {
      default: 'border-pl-gray-200 bg-white text-pl-gray-800 shadow-2xs hover:bg-pl-gray-50 focus:bg-pl-gray-50 dark:bg-pl-neutral-800 dark:border-pl-neutral-700 dark:text-white dark:hover:bg-pl-neutral-700 dark:focus:bg-pl-neutral-700',
      primary: 'border-transparent bg-pl-blue-600 text-white hover:bg-pl-blue-700 focus:bg-pl-blue-700',
      outline: 'border-pl-gray-200 text-pl-gray-500 hover:border-pl-blue-600 hover:text-pl-blue-600 focus:border-pl-blue-600 focus:text-pl-blue-600 dark:border-pl-neutral-700 dark:text-pl-neutral-400 dark:hover:text-pl-blue-500 dark:hover:border-pl-blue-600 dark:focus:text-pl-blue-500 dark:focus:border-pl-blue-600',
      ghost: 'border-transparent text-pl-blue-600 hover:bg-pl-blue-100 hover:text-pl-blue-800 focus:bg-pl-blue-100 focus:text-pl-blue-800 dark:text-pl-blue-500 dark:hover:bg-pl-blue-800/30 dark:hover:text-pl-blue-400 dark:focus:bg-pl-blue-800/30 dark:focus:text-pl-blue-400',
      soft: 'border-transparent bg-pl-blue-100 text-pl-blue-800 hover:bg-pl-blue-200 focus:bg-pl-blue-200 dark:text-pl-blue-400 dark:hover:bg-pl-blue-900 dark:focus:bg-pl-blue-900',
      link: 'border-transparent text-pl-blue-600 hover:text-pl-blue-800 focus:text-pl-blue-800 dark:text-pl-blue-500 dark:hover:text-pl-blue-400 dark:focus:text-pl-blue-400',
      none: '',
      danger: 'border border-transparent bg-red-500 text-white hover:bg-red-600 focus:outline-hidden focus:bg-red-600 disabled:opacity-50 disabled:pointer-events-none',
      outlineBlue: 'inline-flex items-center gap-x-2 text-sm font-medium rounded-lg border border-blue-600 text-blue-600 hover:border-blue-500 hover:text-blue-500 focus:outline-hidden focus:border-blue-500 focus:text-blue-500 disabled:opacity-50 disabled:pointer-events-none dark:border-blue-500 dark:text-blue-500 dark:hover:text-blue-400 dark:hover:border-blue-400'
    },
    size: {
      default: 'py-3 px-4',
      icon: 'py-3 px-4',
      sm: 'py-2 px-3',
      lg: 'p-4 sm:p-5'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}
const Button: FC<ButtonProps> = ({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  ...restProps
}) => {
  const Comp = asChild ? Slot : ButtonDisplay;
  return <Comp {...restProps} className={cn('relative font-semibold select-none', buttonVariants({
    variant,
    size,
    className
  }))} loading={loading} size={size} data-sentry-element="Comp" data-sentry-component="Button" data-sentry-source-file="button.tsx" />;
};
Button.displayName = 'Button';
const ButtonDisplay: FC<ButtonProps> = props => {
  const {
    className,
    children,
    disabled,
    loading,
    size,
    ...rest
  } = props;
  const buttonClassNames = cn(className, {
    'py-3 px-3': loading && size === 'icon'
  });
  return <button className={buttonClassNames} {...rest} disabled={disabled || loading} data-sentry-component="ButtonDisplay" data-sentry-source-file="button.tsx">
      {loading ? <LoadingIndicator textClassName="text-sm font-semibold" size={size === 'icon' ? 'icon' : undefined} /> : children}
    </button>;
};
export { Button, buttonVariants };